/**
 * Control Plane API Service
 *
 * Handles authentication through the Control Plane.
 * All API calls go through the Node.js server proxy to keep URLs secure.
 */
import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import type { AuthConfig } from '@/auth/authUtils';
import type { TenantConfig } from '@/configs/tenants/types';
import { getRuntimeTenantAlias } from '@/runtimeConfig';

export type AuthProviderConfig = AuthConfig & {
  tenant_id: string;
  tenant_alias?: string;
  tenant_name?: string;
};

export type TenantResponse = {
  tenant_id: string;
  tenant_name: string;
  tenant_alias: string;
  config: TenantConfig | null;
};

export const controlPlaneService = {
  /**
   * Get authentication provider configuration by email domain
   *
   * @param {string} emailDomain - Email domain (e.g., "keystone.ai")
   * @param {boolean} includeTenant - Whether to include tenant context (default: true)
   * @returns {Promise<Object>} Authentication provider configuration with tenant context
   */
  async getAuthByEmailDomain(emailDomain: string, includeTenant: boolean = true): Promise<AuthProviderConfig> {
    const tenantAlias = getRuntimeTenantAlias() ?? '';
    const data = await apiFetch<AuthProviderConfig>(tenantAlias, ApiEndpoint.ControlPlaneAuthByEmailDomain, {
      query: includeTenant ? { include_tenant: true } : undefined,
      pathParams: { emailDomain },
    });

    if (
      !data?.protocol ||
      !data?.authentication_id ||
      !data?.login ||
      // OIDC specific required fields
      (data.protocol === 'oidc' && (!data.login.auth_uri || !data.login.client_id || !data.login.redirect_uri)) ||
      // SAML specific required fields
      (data.protocol === 'saml' && !data.login.start_url)
    ) {
      const message =
        (data as { detail?: string; message?: string })?.detail ||
        (data as { detail?: string; message?: string })?.message ||
        `No authentication provider found for domain: ${emailDomain}`;
      throw new Error(message);
    }

    return data;
  },
};
